import { useEffect, useRef, useState } from "react";
import { Quote, Star } from "lucide-react";
import { Reveal, RevealText } from "@project/user/components/motion";
import { useI18n } from "@project/lib/use-i18n";
import { cn } from "@project/lib/utils";
import { testimonialService } from "@project/services/user/testimonial.service";
import type { FeedbackRecord } from "@project/types/feedback";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@project/components/ui/carousel";

const AUTO_SCROLL_MS = 4500;

function StarRating({ rating }: { rating: number }) {
  const score = Math.min(5, Math.max(0, Math.round(rating)));

  return (
    <div
      className="border-gold/20 bg-gold/10 inline-flex items-center gap-1 rounded-full border px-3 py-1.5"
      aria-label={`${score} out of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, index) => {
        const filled = index < score;
        return (
          <Star
            key={index}
            className={cn(
              "h-4 w-4 shrink-0",
              filled ? "fill-gold text-gold" : "text-gold/30 fill-transparent"
            )}
            strokeWidth={filled ? 0 : 1.5}
          />
        );
      })}
    </div>
  );
}

function TestimonialCard({ item }: { item: FeedbackRecord }) {
  const initial = item.full_name?.trim().charAt(0).toUpperCase() || "?";

  return (
    <div className="card-lift flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <Quote className="text-gold/45 h-9 w-9 shrink-0" />
        <StarRating rating={item.rating ?? 0} />
      </div>

      <p className="mt-5 flex-1 text-sm leading-relaxed text-muted-foreground md:text-base">
        &ldquo;{item.message}&rdquo;
      </p>

      <div className="mt-6 flex items-center gap-3 border-t border-border pt-4">
        <div className="border-primary/20 bg-primary/5 grid h-11 w-11 shrink-0 place-items-center rounded-xl border-l-2 font-serif text-base font-semibold text-primary shadow-md">
          {initial}
        </div>
        <div className="min-w-0">
          <div className="truncate font-semibold text-foreground">
            {item.full_name}
          </div>
          {item.feedback_type && (
            <div className="text-xs uppercase tracking-wider text-primary">
              {item.feedback_type}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function UserTestimonialsCarousel() {
  const { t } = useI18n();
  const [testimonials, setTestimonials] = useState<FeedbackRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [api, setApi] = useState<CarouselApi>();
  const pausedRef = useRef(false);

  useEffect(() => {
    let active = true;
    void testimonialService
      .listEnabled()
      .then(items => {
        if (active) {
          setTestimonials(items);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!api || testimonials.length <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      if (!pausedRef.current) {
        api.scrollNext();
      }
    }, AUTO_SCROLL_MS);

    return () => window.clearInterval(timer);
  }, [api, testimonials.length]);

  if (loading || testimonials.length === 0) {
    return null;
  }

  return (
    <section className="border-border/60 border-t bg-background">
      <div className="mx-auto max-w-site px-4 py-16">
        <div className="text-center">
          <Reveal variant="fade">
            <div className="ornament mb-4" aria-hidden>
              <span className="ornament-dot" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              {t("home.testimonials.eyebrow")}
            </p>
          </Reveal>
          <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            <RevealText delay={120}>
              {t("home.testimonials.heading")}
            </RevealText>
          </h2>
          <Reveal variant="fade" delay={260}>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              {t("home.testimonials.description")}
            </p>
          </Reveal>
        </div>

        <Reveal variant="right" delay={300} className="relative mt-10">
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: testimonials.length > 1 }}
            className="w-full"
            onMouseEnter={() => {
              pausedRef.current = true;
            }}
            onMouseLeave={() => {
              pausedRef.current = false;
            }}
          >
            <CarouselContent className="-ml-4">
              {testimonials.map(item => (
                <CarouselItem
                  key={item.id}
                  className="basis-full pl-4 md:basis-1/2 lg:basis-1/3"
                >
                  <TestimonialCard item={item} />
                </CarouselItem>
              ))}
            </CarouselContent>
            {testimonials.length > 1 && (
              <>
                <CarouselPrevious className="border-primary/20 -left-3 hidden bg-card text-primary hover:bg-primary hover:text-primary-foreground md:flex" />
                <CarouselNext className="border-primary/20 -right-3 hidden bg-card text-primary hover:bg-primary hover:text-primary-foreground md:flex" />
              </>
            )}
          </Carousel>
        </Reveal>
      </div>
    </section>
  );
}
